import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AuthProvider = "local" | "google" | "microsoft" | "apple";

/** Aprovação da imobiliária pelo backoffice — só `approved` libera o portal. */
export type ApprovalStatus = "pending" | "approved" | "rejected";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  role: "agency_admin" | "broker";
  /** Organização (imobiliária) à qual o usuário está vinculado (organization_id). */
  agencyId: string;
  authProvider: AuthProvider;
  /** true quando os dados cadastrais da imobiliária já foram preenchidos. */
  profileCompleted: boolean;
  /** true quando os termos obrigatórios já foram aceitos. */
  termsAccepted: boolean;
  /** Estado da aprovação da imobiliária pelo backoffice. */
  approvalStatus: ApprovalStatus;
}

interface AuthState {
  // Access token é apenas em memória — NUNCA persistido (OWASP A02 / ADR-0017).
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setSession: (params: { accessToken: string; user: AuthUser }) => void;
  /** Atualiza campos do usuário logado (ex.: liberar gates do onboarding). */
  patchUser: (patch: Partial<AuthUser>) => void;
  refresh: () => Promise<string | null>;
  logout: () => Promise<void>;
}

// Renovação única em voo (single-flight): o refresh token (cookie HttpOnly `rt`) é
// ROTACIONADO no primeiro uso — dois POST /auth/refresh concorrentes derrubam a
// sessão. Compartilhada por TODOS os chamadores (interceptor axios e cliente SSE).
let refreshInFlight: Promise<string | null> | null = null;

const isMockAuth =
  import.meta.env.VITE_MOCK_AUTH === "true" || import.meta.env.VITE_MOCK_AGENCY === "true";

const mockUser: AuthUser = {
  id: "usr_mock",
  name: "Dono (Mock)",
  email: "dono@mock.com",
  role: "agency_admin",
  agencyId: "ag_mock",
  authProvider: "local",
  profileCompleted: true,
  termsAccepted: true,
  approvalStatus: "approved",
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // O access token de mock vive só em memória (igual ao real); nunca em localStorage.
      accessToken: isMockAuth ? "mock-jwt" : null,
      user: isMockAuth ? mockUser : null,
      isAuthenticated: isMockAuth,

      setSession: ({ accessToken, user }) =>
        set({ accessToken, user, isAuthenticated: true }),

      patchUser: (patch) =>
        set((state) => (state.user ? { user: { ...state.user, ...patch } } : {})),

      refresh: async () => {
        // Mock-first: não há backend de auth real; mantém o token em memória.
        if (isMockAuth) return get().accessToken;

        refreshInFlight ??= (async () => {
          try {
            const { refresh } = await import("../api");
            // Sem corpo — depende do cookie HttpOnly `rt` enviado automaticamente.
            const result = await refresh();
            set({
              accessToken: result.accessToken,
              user: result.user,
              isAuthenticated: true,
            });
            return result.accessToken;
          } catch {
            set({ accessToken: null, user: null, isAuthenticated: false });
            return null;
          } finally {
            refreshInFlight = null;
          }
        })();
        return refreshInFlight;
      },

      logout: async () => {
        try {
          const { logout } = await import("../api");
          await logout();
        } catch {
          // Revogação no servidor falhou — limpa o estado local de qualquer forma.
        } finally {
          set({ accessToken: null, user: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: "imobgo.agency.auth",
      // Persiste só metadados não sensíveis — tokens NUNCA vão para o localStorage.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
